import {useIntl} from 'react-intl';
import {Phone, Users, Video} from 'lucide-react';
import {RTC_PROTOCOL_V1} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {useCallBlock} from '../../groupCalls/hooks/useGroupCall';
import {openRoomCallLobby} from '../../groupCalls/components/groupCallActions';
import {countCallPeople} from '../../groupCalls/services/callPeople';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {CallAvatar} from '../components/CallAvatar';
import {useElapsed} from '../hooks/useElapsed';
import type {ActiveCallRow} from './activeCalls';
import {CallBlockCard} from './CallBlockCard';
import {useActiveCalls} from './useActiveCalls';
import {useCallPeopleText} from './useCallPeopleText';
import {callsMessages as m} from './callsMessages';

const roomNameOf = (roomId: string): string => matrixService.getRoom(roomId)?.name || roomId;

const memberNameOf = (roomId: string, userId: string): string =>
    matrixService.getRoom(roomId)?.getMember(userId)?.name || userId;

const RowButton = ({row, roomName}: {row: ActiveCallRow; roomName: string}) => {
    const intl = useIntl();
    if (row.inThisCall) {
        return (
            <button
                type="button"
                data-testid="active-call-return"
                onClick={() => groupCallService.setMinimized(false)}
                className="phq-call-press inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3.5 text-sm font-semibold text-[var(--phq-call-ink)]"
            >
                {intl.formatMessage(m.activeReturn)}
            </button>
        );
    }
    if (row.full) {
        return <span className="shrink-0 text-[13px] font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.activeNoSpots)}</span>;
    }
    const Icon = row.intent === 'video' ? Video : Phone;
    return (
        <button
            type="button"
            data-testid="active-call-join"
            aria-label={intl.formatMessage(m.joinRoomAria, {room: roomName})}
            onClick={() => openRoomCallLobby(row.roomId, row.intent, 'calls')}
            className="phq-call-press inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] bg-[var(--phq-call-brand)] px-3.5 text-sm font-semibold text-white"
        >
            <Icon size={16} aria-hidden="true"/>
            {intl.formatMessage(m.join)}
        </button>
    );
};

const ActiveCallItem = ({row}: {row: ActiveCallRow}) => {
    const intl = useIntl();
    const roomName = roomNameOf(row.roomId);
    const people = useCallPeopleText(row.roomId, row.summary?.userIds ?? [], 2);
    const timer = useElapsed(row.summary?.startedAt ?? (row.invite ? Date.parse(row.invite.createdAt) || undefined : undefined));
    const count = row.summary ? countCallPeople(row.summary.userIds) : undefined;
    const caller = row.invite ? memberNameOf(row.roomId, row.invite.callerMatrixUserId) : undefined;
    const lead = row.full
        ? intl.formatMessage(m.activeFullCount, {count: count ?? 0, max: RTC_PROTOCOL_V1.maxParticipants})
        : row.invite
            ? intl.formatMessage(m.activeInvited)
            : people;
    const details = [lead, !row.full && count !== undefined ? intl.formatMessage(m.inCall, {count}) : undefined, timer].filter(Boolean).join(' · ');
    return (
        <li className="flex h-12 shrink-0 items-center gap-3 rounded-[10px] px-2" data-testid="active-call-row" data-room-id={row.roomId}>
            {row.invite && caller ? (
                <CallAvatar identity={{name: caller, userId: row.invite.callerMatrixUserId}} size={32}/>
            ) : (
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--phq-call-brand-tint)] text-[var(--phq-call-brand-ink)]">
                    <Users size={16} aria-hidden="true"/>
                </span>
            )}
            <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-[var(--phq-call-ink)]">
                    <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-[#16A34A]"/>
                    <span className="truncate">{roomName}</span>
                </p>
                <p className="truncate text-[13px] tabular-nums text-[var(--phq-call-ink-muted)]">{details}</p>
            </div>
            <RowButton row={row} roomName={roomName}/>
        </li>
    );
};

const SkeletonRow = () => (
    <li className="flex h-12 items-center gap-3 px-2" aria-hidden="true">
        <span className="h-8 w-8 shrink-0 animate-pulse rounded-[10px] bg-[var(--phq-call-surface-muted)]"/>
        <span className="flex flex-1 flex-col gap-1.5">
            <span className="h-3 w-1/3 animate-pulse rounded bg-[var(--phq-call-surface-muted)]"/>
            <span className="h-3 w-1/2 animate-pulse rounded bg-[var(--phq-call-surface-muted)]"/>
        </span>
    </li>
);

export const ActiveCallsSection = () => {
    const intl = useIntl();
    const {rows, status, retry} = useActiveCalls();
    const block = useCallBlock();
    const showBlock = block?.origin === 'calls';
    const loading = status === 'loading' && rows.length === 0;
    if (rows.length === 0 && !loading && status !== 'error' && !showBlock) {
        return null;
    }
    return (
        <section aria-label={intl.formatMessage(m.activeTitle)} data-testid="active-calls" className="phq-call-light mx-2 shrink-0 border-b border-solid border-[var(--phq-call-line)] pb-2">
            <div className="flex items-center gap-2 px-4 pb-1 pt-2 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">
                {intl.formatMessage(m.activeTitle)}
                {rows.length > 0 && (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--phq-call-brand-tint)] px-1.5 text-xs font-semibold text-[var(--phq-call-brand-ink)]">
                        {rows.length}
                    </span>
                )}
            </div>
            {showBlock && block && <CallBlockCard block={block} className="mx-2 mb-2"/>}
            {loading ? (
                <ul className="flex flex-col" aria-label={intl.formatMessage(m.activeLoading)} aria-busy="true">
                    <SkeletonRow/>
                    <SkeletonRow/>
                </ul>
            ) : (
                <ul className="flex flex-col">
                    {rows.map(row => <ActiveCallItem key={row.roomId} row={row}/>)}
                </ul>
            )}
            {status === 'error' && (
                <div role="alert" className="flex items-center gap-3 px-2 py-2 text-[13px] text-[var(--phq-call-ink-muted)]">
                    <span className="flex-1">{intl.formatMessage(m.activeError)}</span>
                    <button
                        type="button"
                        data-testid="active-calls-retry"
                        onClick={retry}
                        className="phq-call-press inline-flex h-8 items-center rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-sm font-semibold text-[var(--phq-call-ink)]"
                    >
                        {intl.formatMessage(m.tryAgain)}
                    </button>
                </div>
            )}
        </section>
    );
};
